import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Users,
  FileText,
  UserCircle,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

interface AdminSidebarProps {
  open: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  requiredRoles?: string[];
  description?: string;
}

export function AdminSidebar({ open, onClose }: AdminSidebarProps) {
  const location = useLocation();
  const { hrUser } = useAuth();

  const navigation: NavItem[] = [
    {
      label: 'แดชบอร์ด',
      href: '/admin',
      icon: LayoutDashboard,
      description: 'ภาพรวมและสถิติ'
    },
    {
      label: 'ใบสมัคร',
      href: '/admin/applications',
      icon: FileText,
      description: 'จัดการใบสมัครงาน'
    },
    {
      label: 'ผู้สมัครงาน',
      href: '/admin/applicants',
      icon: UserCircle,
      requiredRoles: ['HR_ADMIN', 'GROUP_ADMIN'],
      description: 'จัดการข้อมูลผู้สมัครงาน'
    },
    {
      label: 'บริษัท',
      href: '/admin/companies',
      icon: Building2,
      requiredRoles: ['HR_ADMIN', 'GROUP_ADMIN'],
      description: 'จัดการข้อมูลบริษัท'
    },
    {
      label: 'จัดการผู้ใช้',
      href: '/admin/users',
      icon: Users,
      requiredRoles: ['GROUP_ADMIN'],
      description: 'จัดการผู้ใช้ HR และสิทธิ์'
    }
    // Roles & Permissions menu item hidden as requested
    // {
    //   label: 'สิทธิ์และบทบาท',
    //   href: '/admin/permissions',
    //   icon: Shield,
    //   requiredRoles: ['GROUP_ADMIN'],
    //   description: 'จัดการสิทธิ์และบทบาทในระบบ'
    // },
    // Settings menu item hidden as requested
    // {
    //   label: 'การตั้งค่า',
    //   href: '/admin/settings',
    //   icon: Settings,
    //   description: 'การกำหนดค่าระบบ'
    // }
  ];

  const filteredNavigation = navigation.filter(item => {
    if (!item.requiredRoles) return true;
    return item.requiredRoles.includes(hrUser?.role || '');
  });

  const isActive = (href: string) => {
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };


  return (
    <>
      {/* Sidebar */}
      <div className={cn(
        "fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full"
      )}>
        {/* Header */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-gray-900">แผงผู้ดูแลระบบ</span>
          </div>
          
          {/* Close button for mobile */}
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={onClose}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>


        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {filteredNavigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            
            return (
              <Link
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={cn(
                  "flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                  "hover:bg-gray-100 group",
                  active 
                    ? "bg-blue-50 text-blue-700 border border-blue-200" 
                    : "text-gray-700 hover:text-gray-900"
                )}
              >
                <Icon className={cn(
                  "w-5 h-5 mr-3 flex-shrink-0",
                  active ? "text-blue-700" : "text-gray-400 group-hover:text-gray-600"
                )} />
                <span className="flex-1">{item.label}</span>
                {item.badge && (
                  <Badge variant="destructive" className="ml-2 text-xs">
                    {item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4">
          <div className="text-xs text-gray-500 text-center">
            <p>แผงผู้ดูแลระบบ v1.0</p>
            <p className="mt-1">ระบบสมัครงานหลายบริษัท</p>
          </div>
        </div>
      </div>
    </>
  );
}